import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  useDocumentSearch,
  useProcessingEstimate,
  useRoomDocuments,
  useSellerRoomAccess,
  useSession,
} from '@/api/hooks';
import {
  DueSoonCard,
  ExclusivityCard,
  ProcessingCard,
  ProgressCard,
  WaitingOnYouCard,
} from '@/components/DashboardCards';
import { loiAccess, sellerRooms } from '@/config';
import { RequestChecklist } from '@/components/RequestChecklist';
import { SellerGuide } from '@/components/SellerGuide';
import { UploadZone } from '@/components/UploadZone';
import { isContainerUpload, oversizeMessage } from '@/lib/file';
import { UnreadableFilesCard } from '@/components/UnreadableFiles';
import {
  ApiError,
  beginUpload,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  HelpTip,
  createUploadQueue,
  createLandedUploadFinder,
  SearchBar,
  hasSearchCriteria,
  ErrorText,
  errorMessage,
  formatDate,
  isRetryableUploadError,
  sendRoomUpload,
  skippedUploadsNotice,
  summarizeUploads,
  UploadSummary,
} from '@dataroom/shared';
import { SellerDocStatus } from '@/components/SellerDocStatus';
import type {
  IngestFinalizeResponse,
  RoomDocument,
  UploadJob,
  UploadOutcome,
  UploadQueue,
} from '@dataroom/shared';

type UploadState = 'queued' | 'uploading' | 'done' | 'error';

interface UploadResult {
  id: number;
  filename: string;
  state: UploadState;
  message?: string;
  /** The room already held these exact bytes; nothing was uploaded. */
  skipped?: boolean;
  /** The picked file, kept until it is uploaded — so a failed one can be sent again. */
  file?: File;
  /** It failed for a passing reason (connection, busy server): "Try again" is offered. */
  retryable?: boolean;
}

const RESULT_STYLES: Record<UploadState, string> = {
  queued: 'text-muted-foreground',
  uploading: 'text-muted-foreground',
  done: 'text-st-certified-fg',
  error: 'text-destructive',
};

/**
 * Resolves the seller's room from the session identity, then renders the room's
 * dashboard for it: the drop zone on top (uploading stays one step), then progress,
 * what is due soon, what waits on the seller, exclusivity and processing, then the
 * checklist items and the submitted documents. Keeps room-scoped data hooks in a child ({@link SubmitPageInner})
 * so they mount only once a concrete roomId exists (Rules of Hooks) — the room
 * is no longer a hardcoded stub (slice 2).
 */
export function SubmitPage() {
  const access = useSellerRoomAccess();
  if (access.status === 'login') return <Navigate to="/login" replace />;
  if (access.status === 'loading') {
    return (
      <p className="mx-auto w-full max-w-container px-4 py-6 md:px-6 text-sm text-muted-foreground">
        Loading…
      </p>
    );
  }
  if (access.status === 'forbidden') return <Navigate to="/" replace />;
  return <SubmitPageInner roomId={access.roomId} />;
}

function SubmitPageInner({ roomId }: { roomId: string }) {
  const navigate = useNavigate();
  const documents = useRoomDocuments(roomId);
  const session = useSession();
  // Only the owners vote and see the exclusivity (a team member sees no LOI data).
  const isOwner =
    loiAccess(sellerRooms(session.data).find((r) => r.roomId === roomId)) === 'seller';
  const [results, setResults] = useState<UploadResult[]>([]);
  const resultsRef = useRef<UploadResult[]>(results);
  resultsRef.current = results;
  const [query, setQuery] = useState('');
  const idRef = useRef(0);
  /** The one upload queue every pick joins (created on first use), and each
   * queued file's "upload in progress" marker (the new-version banner waits). */
  const queueRef = useRef<UploadQueue | null>(null);
  const endsRef = useRef(new Map<number, () => void>());
  const roomRef = useRef(roomId);
  roomRef.current = roomId;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const qc = useQueryClient();
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** New uploads show in the list, an active search and the checklist (an upload
   * may be matched to a requested item) — refreshed at most once a second while
   * files keep landing, not once per file (a folder is hundreds of files). */
  function refreshSoon() {
    refreshTimer.current ??= setTimeout(() => {
      refreshTimer.current = null;
      const room = roomRef.current;
      for (const key of ['documents', 'search', 'dd-requests', 'dd-summary']) {
        void qc.invalidateQueries({ queryKey: ['rooms', room, key] });
      }
    }, 1_000);
  }

  const searchParams = { query };
  const searchActive = hasSearchCriteria(searchParams);
  const search = useDocumentSearch(roomId, searchParams);

  const outcomes = results.map((r): UploadOutcome => ({
    name: r.filename,
    kind:
      r.state === 'error'
        ? 'failed'
        : r.state === 'done'
          ? r.skipped
            ? 'skipped'
            : 'added'
          : 'pending',
    ...(r.state === 'error' && r.message ? { detail: r.message } : {}),
  }));
  // "Processing these will take about N minutes" (owner ask 2026-10-23): read the
  // room's estimate once the batch is done and something was added.
  const batch = summarizeUploads(outcomes);
  const processingEstimate = useProcessingEstimate(roomId, {
    enabled: batch.done && batch.added > 0,
  });

  const unauthorized = [documents.error, search.error].some(
    (e) => e instanceof ApiError && e.status === 401,
  );
  if (unauthorized) {
    return <Navigate to="/login" replace />;
  }

  function patch(id: number, next: Partial<UploadResult>) {
    setResults((prev) => prev.map((r) => (r.id === id ? { ...r, ...next } : r)));
  }

  /**
   * The seller's files go through the same shared upload queue as the admin's
   * panel (`createUploadQueue`, @dataroom/shared): raw bytes, many at once, each
   * file retried on its own after a passing failure — and more can be added at
   * any time, while earlier files are still going.
   */
  function uploadQueue(): UploadQueue {
    queueRef.current ??= createUploadQueue<IngestFinalizeResponse>({
      send: (file) => sendRoomUpload(roomRef.current, file),
      // A trial-cap refusal after a lost answer: is the file in the room after all?
      confirmLanded: createLandedUploadFinder(() => roomRef.current),
      onStart: (id) => patch(id, { state: 'uploading' }),
      onSuccess: (id, res, { mayHaveLandedEarlier }) => {
        const name = resultsRef.current.find((r) => r.id === id)?.filename ?? '';
        // "Already in the room" after an attempt whose answer was lost means
        // that attempt landed — it is this upload, not a duplicate.
        const skipped = res.skipped && !mayHaveLandedEarlier;
        refreshSoon();
        patch(
          id,
          skipped
            ? { state: 'done', message: 'Already in this room', skipped: true, file: undefined }
            : {
                state: 'done',
                // A zip / saved email is unpacked on the server, each file filed on its own.
                message: isContainerUpload(name)
                  ? 'Submitted — we’ll file each file inside'
                  : 'Submitted',
                file: undefined,
              },
        );
      },
      // The session ended (the first 401 stops the whole queue): sign in again.
      onSessionExpired: () => navigateRef.current('/login'),
      onFailure: (id, err) => {
        if (err instanceof ApiError && err.status === 401) return;
        patch(id, {
          state: 'error',
          message: errorMessage(err) ?? 'Upload failed.',
          retryable: isRetryableUploadError(err),
        });
      },
      onSettled: (id) => {
        endsRef.current.get(id)?.();
        endsRef.current.delete(id);
      },
    });
    return queueRef.current;
  }

  function enqueue(jobs: UploadJob[]) {
    // Each file counts as an upload in progress until it ends (the new-version
    // banner says to reload after the uploads finish).
    for (const job of jobs) endsRef.current.set(job.id, beginUpload());
    uploadQueue().add(jobs);
  }

  function handleFiles(files: File[]) {
    const batch = files.map((file) => ({ id: (idRef.current += 1), file }));
    setResults((prev) => [
      ...batch.map(({ id, file }): UploadResult => {
        const tooBig = oversizeMessage(file);
        return tooBig
          ? { id, filename: file.name, state: 'error', message: tooBig }
          : { id, filename: file.name, state: 'queued', file };
      }),
      ...prev,
    ]);
    enqueue(batch.filter(({ file }) => !oversizeMessage(file)));
  }

  /** "Try again" on a failed file: the same file joins the queue again. */
  function retry(id: number) {
    const file = resultsRef.current.find((r) => r.id === id)?.file;
    if (!file) return;
    patch(id, { state: 'queued', message: undefined, retryable: undefined });
    enqueue([{ id, file }]);
  }

  const skippedNotice = skippedUploadsNotice(results.filter((r) => r.skipped).length);
  const listPending = searchActive ? search.isPending : documents.isPending;
  const listError = searchActive ? search.error : documents.error;
  const docs = searchActive ? (search.data ?? []) : (documents.data ?? []);

  return (
    <div className="mx-auto w-full max-w-container px-4 py-6 md:px-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>
            Upload your documents <HelpTip id="seller.dashboard.upload" className="ml-1" />
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Every file you add is read, filed in the data room and matched to the checklist below
            for you.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <UploadZone onFiles={handleFiles} />

          <UploadSummary
            outcomes={outcomes}
            processingEstimate={processingEstimate.data}
            summaryAside={<HelpTip id="seller.dashboard.upload-summary" />}
            estimateAside={<HelpTip id="seller.dashboard.processing-estimate" />}
          />

          {results.length > 0 ? (
            <ul className="space-y-1 text-sm">
              {results.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-4">
                  <span className="truncate">{r.filename}</span>
                  <span className="flex shrink-0 items-center gap-2">
                    <span className={RESULT_STYLES[r.state]}>
                      {r.state === 'queued'
                        ? 'Queued'
                        : r.state === 'uploading'
                          ? 'Uploading…'
                          : (r.message ?? (r.state === 'done' ? 'Submitted' : 'Failed'))}
                    </span>
                    {r.state === 'error' && r.retryable && r.file ? (
                      <button
                        type="button"
                        onClick={() => retry(r.id)}
                        aria-label={`Try uploading ${r.filename} again`}
                        className="text-xs font-medium text-primary hover:underline"
                      >
                        Try again
                      </button>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
          {skippedNotice ? (
            <p role="status" className="text-sm text-muted-foreground">
              {skippedNotice}
            </p>
          ) : null}
        </CardContent>
      </Card>

      {/* First visit to the room: a short guide under the drop zone (which stays on top),
          hidden once done or dismissed. */}
      {session.data && documents.data ? (
        <SellerGuide roomId={roomId} isOwner={isOwner} hasDocuments={documents.data.length > 0} />
      ) : null}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-1 lg:col-start-3 lg:row-start-1">
          <WaitingOnYouCard
            roomId={roomId}
            isOwner={isOwner}
            documents={documents.data ?? []}
            documentsPending={documents.isPending}
          />
          {isOwner ? <ExclusivityCard roomId={roomId} /> : null}
          <ProcessingCard roomId={roomId} />
        </div>
        <div className="min-w-0 space-y-6 lg:col-span-2 lg:col-start-1 lg:row-start-1">
          <ProgressCard roomId={roomId} />
          <DueSoonCard roomId={roomId} documents={documents.data ?? []} />
        </div>
      </div>

      <UnreadableFilesCard roomId={roomId} documents={documents.data ?? []} />

      <RequestChecklist roomId={roomId} documents={documents.data ?? []} />

      <Card>
        <CardHeader>
          <CardTitle>Submitted documents</CardTitle>
          <SearchBar onQueryChange={setQuery} searching={search.isFetching} className="pt-2" />
        </CardHeader>
        <CardContent>
          {listPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : listError ? (
            <ErrorText error={listError} />
          ) : docs.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 font-medium">Document</th>
                  <th className="py-2 font-medium">Submitted</th>
                  <th className="py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {docs.map((doc: RoomDocument) => (
                  <tr key={doc.id} className="border-b last:border-0">
                    <td className="py-2 pr-4 font-medium">{doc.name}</td>
                    <td className="py-2 pr-4">{formatDate(doc.createdAt)}</td>
                    <td className="py-2">
                      <SellerDocStatus status={doc.status} unreadable={doc.unreadable} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-muted-foreground">
              {searchActive
                ? 'No submitted documents match your search.'
                : 'Nothing submitted yet. Upload your first document above.'}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
